import React, { useState } from 'react'
import classNames from 'classnames'
import { isEmpty } from 'lodash'

import { Checkbox } from '@/components/forms/Checkbox'
import { Table } from '@/components/tables/Table/Table'
import { TreeTableRow } from './TreeTableRow'

import './TreeTable.scss'

/**
 * @import import { TreeTable } from '@/components/tables/TreeTable/TreeTable'
 * @param {object} props
 * @param {Array<object>} props.rows Row data keyed by column accessor; a cell value is a node, or { as, cellProps } to spread attributes onto its td. Nested rows live under childRows, at any depth. Optional row fields: hasChildren (shows a toggle before childRows load), childRowsLoading (a loading row under the expanded row), onShowMore (a Show more button under the expanded row, hidden while loading), hideCheckbox (drops that row's checkbox), checkboxDisabled (disables it)
 * @param {Array<object>} props.columns Columns with accessor, label, and optional headerAlignment ('right' right-aligns the header and its cells)
 * @param {string} [props.rowKey='id'] Row field holding the unique row id
 * @param {boolean} [props.defaultExpandAll] Starts every row expanded
 * @param {(row: object) => void} [props.onRowToggle] Enables a checkbox on every row; the consumer owns each row's selected and indeterminate flags
 * @param {(row: object) => void} [props.onExpand] Called with the row each time the user expands it, never on collapse or for defaultExpandAll's initial state
 * @param {(row: object) => string} [props.checkboxLabel] Accessible name of a row's checkbox; defaults to `Select row ${id}` because the first cell can be a React node
 * @param {object} [props.headerCheckbox] Controlled select-all checkbox with checked, indeterminate, onChange
 */
export const TreeTable = ({
  rows,
  columns,
  rowKey = 'id',
  defaultExpandAll = false,
  onRowToggle,
  onExpand,
  checkboxLabel = (row) => `Select row ${row[rowKey]}`,
  headerCheckbox,
  ...props
}) => {
  // With defaultExpandAll, toggledRowIds holds collapsed ids instead of
  // expanded ones, so rows that arrive after mount still start expanded
  const [toggledRowIds, setToggledRowIds] = useState([])

  const isRowExpanded = (rowId) => {
    if (defaultExpandAll) {
      return !toggledRowIds.includes(rowId)
    }
    return toggledRowIds.includes(rowId)
  }

  const handleExpandToggle = (row) => {
    const rowId = row[rowKey]

    if (!isRowExpanded(rowId)) {
      onExpand?.(row)
    }

    setToggledRowIds((prevState) =>
      prevState.includes(rowId) ? prevState.filter((id) => id !== rowId) : [...prevState, rowId]
    )
  }

  const selectable = Boolean(onRowToggle)
  const hasToggles = rows.some((row) => !isEmpty(row.childRows) || Boolean(row.hasChildren))

  return (
    <Table className="tree-table" {...props}>
      <thead>
        <tr>
          {columns.map((column, columnIndex) => (
            <th
              key={column.accessor}
              className={classNames({ 'right-aligned': column.headerAlignment === 'right' })}
            >
              {columnIndex === 0 ? (
                <div className="flex-align-center medium-gap">
                  {hasToggles && <span className="tree-expand-spacer" />}
                  {selectable && headerCheckbox && (
                    <Checkbox
                      data-testid="header-row-select"
                      aria-label="Select all"
                      checked={!!headerCheckbox.checked}
                      indeterminate={!!headerCheckbox.indeterminate}
                      onChange={headerCheckbox.onChange}
                    />
                  )}
                  {column.label}
                </div>
              ) : (
                column.label
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <TreeTableRow
            key={row[rowKey]}
            row={row}
            columns={columns}
            rowKey={rowKey}
            depth={0}
            showToggle={hasToggles}
            selectable={selectable}
            checkboxLabel={checkboxLabel}
            onRowToggle={onRowToggle}
            isRowExpanded={isRowExpanded}
            onExpandToggle={handleExpandToggle}
          />
        ))}
      </tbody>
    </Table>
  )
}
